// Chip-input multi-select for relationship arrays (e.g. car → attribute
// values). Same searchFn contract as RelationshipPicker but holds an array
// of selected RelationshipOption.
//
// Selected items render as removable chips. The input below stays open
// for additions; selected ids are excluded from search results so the
// user can't pick the same value twice.

import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RelationshipOption } from './RelationshipPicker';

interface MultiRelationshipPickerProps {
  label: string;
  /** Currently-selected items, in the order the user added them. */
  value: RelationshipOption[];
  onChange: (next: RelationshipOption[]) => void;
  searchFn: (q: string, signal?: AbortSignal) => Promise<RelationshipOption[]>;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  emptyLabel?: string;
}

export function MultiRelationshipPicker({
  label,
  value,
  onChange,
  searchFn,
  placeholder = 'Type to search…',
  hint,
  disabled,
  emptyLabel = 'No matches.',
}: MultiRelationshipPickerProps) {
  const reactId = useId();
  const inputId = `multirel-${reactId}`;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useQuery({
    queryKey: ['multi-rel-picker', label, debounced],
    queryFn: ({ signal }) => searchFn(debounced, signal),
    enabled: open && debounced.length > 0,
    staleTime: 30_000,
  });

  const selectedIds = new Set(value.map((v) => v.id));
  const visible = (results.data ?? []).filter((r) => !selectedIds.has(r.id));

  const addOption = (opt: RelationshipOption) => {
    if (selectedIds.has(opt.id)) return;
    onChange([...value, opt]);
    setQuery('');
  };
  const removeId = (id: number) => onChange(value.filter((v) => v.id !== id));

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-1">
      <label htmlFor={inputId} className="text-[11px] font-medium uppercase tracking-wide text-[color:var(--bo-mute)]">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-[color:var(--bo-line)] bg-white px-2 py-1.5 focus-within:border-[color:var(--bo-mute)]">
        {value.map((opt) => (
          <span
            key={opt.id}
            className="inline-flex items-center gap-1 rounded bg-[color:var(--bo-bg)] px-2 py-0.5 text-[12px] text-[color:var(--bo-ink)]"
          >
            {opt.sub ? (
              <span className="text-[10px] uppercase tracking-wide text-[color:var(--bo-mute)]">
                {opt.sub} ·
              </span>
            ) : null}
            <span className="font-medium">{opt.label}</span>
            <button
              type="button"
              onClick={() => removeId(opt.id)}
              disabled={disabled}
              aria-label={`Remove ${opt.label}`}
              className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-[color:var(--bo-mute)] hover:bg-black/5 hover:text-[color:var(--bo-ink)] disabled:opacity-50"
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={inputId}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
          autoComplete="off"
          className="min-w-[120px] flex-1 border-0 bg-transparent text-[13px] text-[color:var(--bo-ink)] outline-none placeholder:text-[color:var(--bo-mute)]"
        />
      </div>
      {hint ? <span className="text-[11px] text-[color:var(--bo-mute)]">{hint}</span> : null}

      {open ? (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-md border border-[color:var(--bo-line)] bg-white shadow-lg">
          {debounced.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">
              Start typing to search…
            </div>
          ) : results.isLoading ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">Searching…</div>
          ) : results.error ? (
            <div className="px-3 py-2 text-[12px] text-[#9f1239]">
              {(results.error as Error).message}
            </div>
          ) : visible.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-[color:var(--bo-mute)]">{emptyLabel}</div>
          ) : (
            <ul className="divide-y divide-[color:var(--bo-line)]">
              {visible.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => addOption(opt)}
                    className="block w-full px-3 py-2 text-left text-[13px] text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-bg)]"
                  >
                    <div className="truncate font-medium">{opt.label}</div>
                    {opt.sub ? (
                      <div className="truncate text-[11px] text-[color:var(--bo-mute)]">
                        {opt.sub}
                      </div>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
